import { Component, Vue, Prop, Watch, Inject } from 'vue-property-decorator';
import ErrorAlert from '@/components/errors/ErrorAlert.vue';
import ValidationErrors from '@/components/errors/ValidationErrors.vue';
import FieldValidationErrors from '@/components/errors/FieldValidationErrors.vue';
import EmailsInput from '@/components/inputs/email/EmailsInput.vue';
import { Attendee } from '@/common/types/event/DtoAttendee';
import '@/common/filters/AllFiltersExport';
import { TicketsAndAddons } from '../../common/types/event/DtoTicketsAndAddons';
import { Ticket } from '../../common/types/event/DtoTicket';
import { EventRegistration } from '../../common/types/event/DtoEventRegistration';
import { PaymentContainerToken, PaymentProcessorOptions, createPaymentProcessorOptions, DtoEvent, PaymentTypeEnum } from '../../common/types/AllTypesExports';
import { eventRepo } from '../../common/repositories/EventRepo';
import { AxiosResponse } from 'axios';
import { ApiPaymentErrorResponse, createApiPaymentErrorResponse } from '../../common/types/Payment/ApiPaymentErrorResponse';
import PaymentContainer from '@/components/payment-container/PaymentContainer.vue';
import { default as PaymentContainerClass } from '@/components/payment-container/PaymentContainerVue';
import { DtoRegistrationResponse } from '@/common/types/event/DtoRegistrationResponse';
import { DtoCurrentUser } from '@/common/types/user/DtoCurrentUser';
import cloneDeep from 'lodash/cloneDeep';
import PromoCodeInput from '@/components/inputs/promo/PromoCodeInput.vue';
import PhoneInput from '@/components/inputs/phone/PhoneInput.vue';
import RecaptchaInput from '@/components/inputs/recaptcha/RecaptchaInput.vue';
import { default as RecaptchaInputClass } from '@/components/inputs/recaptcha/RecaptchaInputVue';
import TermsAndConditionsInput from '@/components/inputs/terms/TermsAndConditionsInput.vue';
import ConsentInput from '@/components/inputs/consent/ConsentInput.vue';
import { Addon } from '../../common/types/event/DtoAddon';
import { IEventRegistrationGlobals } from '../../pages/event-registration/EventRegistrationGlobals';
import { PaymentProcessingStatus } from '../../common/types/Payment/PaymentProcessingStatus';
import { PropOptions } from 'vue';
import { paymentsRepo } from '../../common/repositories/PaymentsRepo';
import CustomerSelector from '@/components/membership/CustomerSelector.vue';

/** Determines when to show a "ticket price will increase" warning on event registration checkout - number of minutes */
export const EVENT_CHECKOUT_PRICE_INCREASE_WARNING_MINUTES = 35;

/** Determines when to show a "ticket price will increase" warning on event registration checkout - number of milliseconds */
export const EVENT_CHECKOUT_PRICE_INCREASE_WARNING_MS = EVENT_CHECKOUT_PRICE_INCREASE_WARNING_MINUTES * 60 * 1000;

declare let noviNs: IEventRegistrationGlobals;


@Component({
    name: 'EventCheckout',
    components: {
        ErrorAlert,
        ValidationErrors,
        FieldValidationErrors,
        PaymentContainer,
        EmailsInput,
        PromoCodeInput,
        PhoneInput,
        RecaptchaInput,
        TermsAndConditionsInput,
        ConsentInput,
        CustomerSelector
    }
})
export default class EventCheckout extends Vue {
    @Inject('event')
    event!: DtoEvent;

    @Prop({ required: true })
    invoiceFee!: number;

    @Inject('registration')
    registration!: EventRegistration;

    @Inject('ticketsAndAddons')
    ticketsAndAddons!: TicketsAndAddons;

    @Inject('user')
    currentUser!: DtoCurrentUser;

    @Inject('validator')
    $validator: any;

    @Prop({ required: false, default: () => null })
    attendeeBased!: boolean;

    @Prop({ required: false, default: () => 'Parent Member' })
    parentLabel!: string;

    @Prop({ required: false, default: () => false })
    parentRequired!: boolean;

    isDonatingTransactionFee: boolean = noviNs.paymentProcessorOptions.donateTransactionFeeChargeType?.toLowerCase() === 'OptOut'.toLowerCase() || noviNs.paymentProcessorOptions.donateTransactionFeeChargeType?.toLowerCase() === 'Mandatory'.toLowerCase();

    convenienceFee: number = 0;
    taxable: boolean = true;
    paymentType: string = PaymentTypeEnum.creditCard.toString();

    emailForConfirmation: { 'guest': string | null } = {
        'guest': null
    };

    isPromoInFocus: boolean = false;

    paymentProcessorOptions: PaymentProcessorOptions = createPaymentProcessorOptions({
        initialTotal: 0,
        enableSaveCreditCard: (this.currentUser.customerID != null && this.currentUser.customerID != undefined) || false,
        enableAutoPay: false,
        enableInvoice: true, // Always show the tab, but display proper warning message
        enableACH: noviNs.paymentProcessorOptions.enableACH,
        enableSaveAch: noviNs.paymentProcessorOptions.enableSaveAch,
        savedAchs: noviNs.paymentProcessorOptions.savedAchs,
        invoiceFee: this.invoiceFee,
        donateTransactionFeeLabel: noviNs.paymentProcessorOptions.donateTransactionFeeLabel,
        donateTransactionFeeDescription: noviNs.paymentProcessorOptions.donateTransactionFeeDescription,
        donateTransactionFeePercentage: noviNs.paymentProcessorOptions.donateTransactionFeePercentage,
        donateTransactionFeeChargeType: noviNs.paymentProcessorOptions.donateTransactionFeeChargeType,
        convenienceFeeTriggerAmount: noviNs.paymentProcessorOptions.convenienceFeeTriggerAmount,
        convenienceFeeIsEnabled: noviNs.paymentProcessorOptions.convenienceFeeIsEnabled,
        hasItemWithDisabledInvoicing: this.hasItemWithDisabledInvoicing(),
        invoiceWarningMessage: this.getInvoiceWarningMessage(),
        alternatePaymentStripeId: noviNs.alternatePaymentStripeId,
        alternatePaymentCurrency: noviNs.alternatePaymentCurrency,
        enableCreditCard: noviNs.paymentProcessorOptions.enableCreditCard,
        enablePaymentPlans: noviNs.paymentProcessorOptions.enablePaymentPlans
    });

    hasItemWithDisabledInvoicing() {
        return this.registration.attendees.every(a => a.selectedTickets.some(t => t.disableInvoicing));
    }

    getInvoiceWarningMessage() {
        if (!this.currentUser.isLoggedIn) {
            return 'You must login in order to be eligible for invoicing.';
        }

        const ticketsWithDisabledInvoicing = this.registration.attendees
            .flatMap(a => a.selectedTickets)
            .filter(t => t.disableInvoicing)
            .map(t => t.name);

        if (ticketsWithDisabledInvoicing.length > 0) {
            return `Invoicing is not allowed for these ticket(s): ${ticketsWithDisabledInvoicing.join(", ")}. Please purchase separately or pay with Credit/Debit or ACH.`;
        }

        return "";
    }

    created() {
        if (this.currentUser.defaultBillablePartyID) {
            this.paymentProcessorOptions.billablePartyId = this.currentUser.defaultBillablePartyID;
        }

        // Ensure when we pass in the alternate payment stripe ID, we disable all other payment options and force CC payment
        if (noviNs.alternatePaymentStripeId) {
            this.paymentProcessorOptions.enableInvoice = false;
            this.paymentProcessorOptions.enableSaveCreditCard = false;
            this.paymentProcessorOptions.enableAutoPay = false;
            this.paymentProcessorOptions.enableACH = false;
            this.paymentProcessorOptions.enableSaveAch = false;
            this.paymentProcessorOptions.enablePaymentPlans = false;
            this.paymentProcessorOptions.savedAchs = [];
            this.paymentProcessorOptions.savedCards = [];
        }

        const contactEmail = this.currentUser.professionalEmail;
        if (contactEmail && this.registration.confirmationEmails.length === 0) {
            this.registration.confirmationEmails = [contactEmail];
        }
    }

    mounted() {
        this.updateTaxRate();

        this.onTotalChanged(this.total);

        if (this.currentUser.customerID) {
            this.registration.purchaserId = this.currentUser.customerID;
        }

        // Populate logged in user's email in confirmation emails input
        if (this.currentUser.professionalEmail && !this.registration.summaryEmails.includes(this.currentUser.professionalEmail)) {
            this.registration.summaryEmails.push(this.currentUser.professionalEmail);
        }

        // Populate selected attendee emails in confirmation emails input
        if (this.registration.attendees.length > 0) {
            this.registration.attendees.forEach(attendee => {
                if (attendee.email && !this.registration.confirmationEmails.includes(attendee.email)) {
                    this.registration.confirmationEmails.push(attendee.email);
                }
            });
        }        

        //filtering summary email in case of going back in stepper to change attendees emails to remove old ones that are not anymore part of emails of attendees
        if (this.registration.summaryEmails && this.registration.summaryEmails.length > 0 && this.registration.attendees.length > 0) {
            if (!this.currentUser.professionalEmail) {
                this.registration.summaryEmails = this.registration.summaryEmails.filter(email =>
                    this.registration.attendees.some(attendee => attendee.email === email));
            }
            else {
                this.registration.summaryEmails = this.registration.summaryEmails.filter(email =>
                    this.registration.attendees.some(attendee => attendee.email === email) || email === this.currentUser.professionalEmail);
            }
        }

        //filtering same thing for confirmation emails in case of going back in stepper
        const contactEmail = this.currentUser.professionalEmail?.trim().toLowerCase();

        if (this.registration.confirmationEmails?.length > 0 && this.registration.attendees.length > 0) {
            this.registration.confirmationEmails = this.registration.confirmationEmails.filter(email =>
                this.registration.attendees.some(attendee => attendee.email === email) || (!!contactEmail && email === contactEmail)
            );
        }

        if (!this.currentUser.isLoggedIn && this.registration.summaryEmails.length == 0) {
            this.registration.summaryEmails.push(this.registration.guestEmail || "");
            
        }

        this.registration.isDonatingTransactionFee = this.isDonatingTransactionFee;
    }

    /** Legacy checkbox reCAPTCHA v2 — only for guests when Enterprise upgrade is disabled. */
    get showRecaptchaV2() {
        return !noviNs.reCaptchaUpgradeEnabled && !this.currentUser.isLoggedIn;
    }

    get taxExempt() {
        return (this.paymentType == PaymentTypeEnum.invoice.toString() && !this.taxable) || (this.paymentType == PaymentTypeEnum.creditCard.toString() && !this.currentUser.taxable)
    }

    onPaymentTokenReady(paymentToken: PaymentContainerToken) {
        if (noviNs.reCaptchaUpgradeEnabled) {
            const input = document.querySelector<HTMLInputElement>('input[name="g-recaptcha-response"]');
            this.registration.recaptchaToken = input?.value ?? null;
        }

        var validationPassed = false;

        this.$validator.validateAll()
            .then((valid: any) => {
                if (valid && !this.errors.any()) {
                    validationPassed = true;
                    let paymentWidget = <PaymentContainerClass>this.$refs.payment;
                    paymentWidget.onErrorToggle(false, '');
                }
                else {
                    validationPassed = false;                    
                    let paymentWidget = <PaymentContainerClass>this.$refs.payment;
                    paymentWidget.onErrorToggle(true, 'Please correct the errors shown before submitting.');
                }
            })
            .then(() => {
                if (!validationPassed)
                    return;

                this.registration.paymentToken = paymentToken;
                eventRepo.register(this.registration)
                    .then((result: DtoRegistrationResponse) => {
                        if (this.currentUser.isLoggedIn) {
                            eventRepo.updateCustomerRegistrationActivityCompleted(this.event.uniqueId, Number(this.currentUser.customerID), true)
                                .then(() => { window.location.href = `/${this.event.fileNameWithPrefix}/confirmation?registrationId=${result.registrationId}` })
                                .catch(() => { window.location.href = `/${this.event.fileNameWithPrefix}/confirmation?registrationId=${result.registrationId}` });
                        }
                        else {
                            eventRepo.updateGuestRegistrationActivityCompleted(this.event.uniqueId, String(this.registration.guestEmail), true)
                                .then(() => { window.location.href = `/${this.event.fileNameWithPrefix}/confirmation?registrationId=${result.registrationId}` })
                                .catch(() => { window.location.href = `/${this.event.fileNameWithPrefix}/confirmation?registrationId=${result.registrationId}` });
                        }
                    })
                    .catch((axiosResponse: AxiosResponse<ApiPaymentErrorResponse>) => {
                        if (axiosResponse.status >= 500) {
                            if (!axiosResponse.data || typeof axiosResponse.data !== 'object') {
                                axiosResponse.data = createApiPaymentErrorResponse(axiosResponse.data);
                                axiosResponse.data.message = `It appears your browser had an issue. In order to continue, please restart your browser and check your email or <a href="${noviNs.contactUsUrl}" target="_blank"><strong>contact us</strong></a> to see if your checkout went through.`;
                            }
                            axiosResponse.data.paymentProcessingStatus = PaymentProcessingStatus.Error;
                        }
                        if (this.showRecaptchaV2) {
                            let recaptchaInput = <RecaptchaInputClass>this.$refs.recaptcha;
                            recaptchaInput.reset();
                        }
                        let paymentWidget = <PaymentContainerClass>this.$refs.payment;
                        paymentWidget.handleApiPaymentError(axiosResponse.data);
                    });
            });        
    }

    get proposedDonateTransactionFeeAmount() {
        let ttl = this.registration.subTotal + this.registration.taxTotal + this.convenienceFee - this.proposedAmountForPaymentPlan;
        if (ttl <= 0) {
            return 0;
        }

        if (this.paymentProcessorOptions.convenienceFeeIsEnabled && (ttl >= this.paymentProcessorOptions.convenienceFeeTriggerAmount)) {
            return ttl * (this.paymentProcessorOptions.donateTransactionFeePercentage || 0);
        }

        return 0;
    }

    get donateTransactionFeeAmount() {
        if ((this.paymentType !== 'credit-card' && this.paymentType !== 'payment-plans') || !this.isDonatingTransactionFee) {
            return 0;
        }

        return this.proposedDonateTransactionFeeAmount;
    }

    get donateTransactionFeeAmountFormatted() {
        return this.registration.currencyIcon + this.donateTransactionFeeAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    get convenienceFeeFormatted() {
        return this.registration.currencyIcon + this.convenienceFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    get eligibleForPaymentPlanAmount() {
        return this.registration.eligibleForPaymentPlanAmount;
    }

    get eligibleItemsForPaymentPlan() {
        return this.registration.eligibleItemsForPaymentPlan;
    }

    get proposedAmountForPaymentPlan() {
        if (this.paymentType !== 'payment-plans') {
            return 0;
        }

        return this.eligibleForPaymentPlanAmount;
    }

    get total() {
        return this.registration.subTotal + this.registration.taxTotal + this.convenienceFee + this.donateTransactionFeeAmount;
    }

    get totalFormatted() {
        return this.registration.currencyIcon + this.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    get showPromoCode() {
        return (this.registration.ticketTotal > 0 || this.registration.addonTotal > 0) && this.event.hasPromos && !this.paymentProcessorOptions.alternatePaymentStripeId;
    }

    get pageLoadEventTimeNowMs(): number {
        const raw = this.event.eventTimeNow;
        const parsed = new Date(raw as any).getTime();
        if (isNaN(parsed)) {
            return Date.now();
        }
        return parsed;
    }

    get ticketsWithImminentPriceIncrease(): Ticket[] {
        const refTime = this.pageLoadEventTimeNowMs;
        const cutoff = refTime + EVENT_CHECKOUT_PRICE_INCREASE_WARNING_MS;
        const matched: Ticket[] = [];

        for (const ticket of this.registration.uniquePurchasedTickets) {
            const pricing = ticket.ticketPricingByDate;
            if (!pricing) {
                continue;
            }
            if (!pricing.nextPriceIncreaseDate || pricing.nextPriceIncreaseValue == null) {
                continue;
            }
            if (pricing.priceToday >= pricing.nextPriceIncreaseValue) {
                continue;
            }

            const increaseAt = new Date(pricing.nextPriceIncreaseDate).getTime();
            if (isNaN(increaseAt)) {
                continue;
            }

            if (increaseAt >= refTime && increaseAt <= cutoff) {
                matched.push(ticket);
            }
        }

        return matched;
    }

    get priceIncreaseWarning(): string {
        try {
            const ticketsWithPriceIncrease = this.ticketsWithImminentPriceIncrease;
            if (ticketsWithPriceIncrease.length === 0) {
                return '';
            }

            const ticketTypesInCart = new Set(this.registration.attendees.flatMap(a => a.selectedTickets.map(t => t.id)));

            if (ticketsWithPriceIncrease.length === 1 && ticketTypesInCart.size === 1) {
                return 'Heads up! The price of the ticket in your cart will increase soon. See the Tickets & Add-ons tab for details.';
            } else if (ticketsWithPriceIncrease.length === 1) {
                return `Heads up! The price of the ${ticketsWithPriceIncrease[0].name} ticket in your cart will increase soon. See the Tickets & Add-ons tab for details.`;
            } else {
                return 'Heads up! The prices of some tickets in your cart will increase soon. See the Tickets & Add-ons tab for details.';
            }
        } catch (e) {
            console.error('[EventCheckout.PriceWarning]', 'Error building price increase warning', e);
            return '';
        }
    }

    @Watch('total', { immediate: true })
    onTotalChanged(newTotal: number) {
        let paymentWidget = <PaymentContainerClass>this.$refs.payment;
        if (paymentWidget) {

            // Why pass in subTotal instead of newTotal?
            // Because the payment container uses the total we supply to determine whether or not this is a free registration
            // If we use newTotal, we'll be including the invoice convenience fee, which would make the total positive on an otherwise free registration
            // By passing in subTotal, the invoice convenience fee will go away if our total is otherwise zero
            paymentWidget.onTotalUpdated(this.registration.subTotal + this.registration.taxTotal + this.donateTransactionFeeAmount); 
        }
    }

    @Watch('proposedDonateTransactionFeeAmount', { immediate: true })
    onProposedDonateTransactionFeeAmountUpdated(value: number) {
        const paymentContainer = this.$refs.payment as PaymentContainerClass;
        if (paymentContainer && paymentContainer.onProposedDonateTransactionFeeAmountUpdated) {
            paymentContainer.onProposedDonateTransactionFeeAmountUpdated(value);
        }
    }

    handleIsDonatingTransactionFeeUpdated(value: boolean) {
        this.isDonatingTransactionFee = value;
        this.registration.isDonatingTransactionFee = value;
    }

    backToFirstStep() {
        this.$emit("backToFirstStep");
    }

    get remainingSeats() {
        if (!this.attendeeBased) {
            return null;
        }

        // Map to keep track of how many seats are taken for each ticket
        const seatsTaken = new Map();

        // Iterate through attendees to count the seats taken for each ticket
        this.registration.attendees.forEach(attendee => {
            attendee.selectedTickets.forEach(ticket => {
                const currentCount = seatsTaken.get(ticket.id) || 0;
                seatsTaken.set(ticket.id, currentCount + 1);
            });
        });

        // Array to accumulate the remaining seats
        const remainingSeats: {
            id: any; name: string; price: number; formatedPrice: string;  // Use the calculated price
        }[] = [];

        var currencyIcon = this.registration.currencyIcon;

        // Now calculate the remaining seats per ticket type
        seatsTaken.forEach((count, ticketId) => {
            // Find the ticket by ID to get the seatsPerTicket value
            const ticket = this.registration.attendees.flatMap(a => a.selectedTickets).find(t => t.id === ticketId);
            if (!ticket) return; // Continue if ticket not found

            const remaining = ticket.seatsPerTicket - count;
            const totalTicketPrice = this.registration.getTicketPrice(ticket, undefined, true);

            // Calculate the base price per seat
            const basePricePerSeat = Math.floor((totalTicketPrice / ticket.seatsPerTicket) * 100) / 100;
            let priceAccumulated = basePricePerSeat * count; // The amount already distributed

            for (let i = 0; i < remaining; i++) {
                let priceForThisSeat = basePricePerSeat;

                // Check if this is the last seat for this ticket type
                if (i === remaining - 1) {
                    // Adjust the price for the last seat to include any remainder
                    priceForThisSeat = totalTicketPrice - priceAccumulated;
                } else {
                    priceAccumulated += priceForThisSeat;
                }

                // Push a placeholder for each remaining seat
                remainingSeats.push({
                    id: ticketId,
                    name: 'TBD',
                    price: priceForThisSeat, // Use the calculated price
                    formatedPrice: currencyIcon + priceForThisSeat.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    })
                });
            }
        });

        return remainingSeats;
    }

    get attendees() {
        return this.registration.attendees;
    }

    getAttendeeName(attendee: Attendee) {
        return attendee.name || attendee.selectedTickets[0].name;
    }

    onGuestEmailChanged() {
        this.updateConfirmationEmails(this.registration.guestEmail, 'guest');
    }

    onConvenienceFeeUpdated(newInvoiceFee: number) {
        this.convenienceFee = newInvoiceFee;
    }

    onBillablePartyTaxableUpdated(taxable: boolean) {
        this.taxable = taxable;
    }

    onPaymentTypeUpdated(paymentType: string) {
        this.paymentType = paymentType;
    }

    updateDiscountError(discountError: string) {
        if (discountError) {
            this.errors.add({
                field: 'discount',
                msg: discountError
            });
        }
        else {
            this.errors.remove('discount');
        }
    }

    updatePromoFocus(isPromoInFocus: boolean) {
        this.isPromoInFocus = isPromoInFocus;
    }

    @Watch('taxExempt', { immediate: true })
    onTaxExemptChanged() {
        this.registration.taxExempt = this.taxExempt;
    }

    showAttendee(attendee: Attendee) {
        this.$emit("showAttendee", attendee);
    }

    showAddon(addon: Addon) {
        this.$emit("showAddon", addon);
    }

    get visibleAddons() {
        return this.registration.selectedAddons.filter(addon => !addon.isDonation || addon.subTotal > 0);
    }
 
    updateConfirmationEmails(enteredAddress: string | null, addressType: keyof typeof EventCheckout.prototype.emailForConfirmation) {
        // Remove the previous address in case it was updated
        if (this.emailForConfirmation[addressType]) {
            this.registration.confirmationEmails = this.registration.confirmationEmails.filter(addr => addr.toLowerCase() !== (this.emailForConfirmation[addressType] || '').toLowerCase())
        }

        if (enteredAddress && !this.registration.confirmationEmails.some((x) => x.toLowerCase() === (enteredAddress || '').toLowerCase())) {
            this.registration.confirmationEmails.push(enteredAddress)

            // Only set this if the address is new to the list
            this.emailForConfirmation[addressType] = enteredAddress
        }
    }

    updateTaxRate(billingState: string = "") {
        paymentsRepo.getTaxRate("", billingState, this.currentUser.customerUniqueID, this.event.id)
            .then((taxRate: any) => {
                this.registration.taxRate = taxRate;
            })
            .catch((axiosResponse: AxiosResponse<Number>) => {
                this.registration.taxRate = this.event.taxRate;
            });
    }

    get guestOnlyAttendee() {
        let emails = this.registration.attendees.map(attendee => attendee.email);
        let firstNonNullEmail = emails.find(email => email !== null);
        return !this.currentUser.isLoggedIn && ((this.registration.attendees.length == 1 && this.registration.attendees[0].email == this.registration.guestEmail) || emails.every(email => email == null || email == firstNonNullEmail));
    }

    get allAttendeesHavePurchaserEmail() {
        const contactEmail = this.currentUser.professionalEmail;
        return !!contactEmail && this.registration.confirmationEmails && this.registration.confirmationEmails.every(e => e?.trim().toLowerCase() === contactEmail);
    }

    get isCheckoutDisabled() {
        return this.registration.attendees.length === 0 || this.isPromoInFocus;
    }

    get showIndividualConfirmationList() {
        const contactEmail = this.currentUser.professionalEmail;
        const hasMyEmail = !!contactEmail && this.registration.confirmationEmails?.some(e => e.trim() === contactEmail);
        const anyAttendeeHasEmail = this.attendees.some(a => !!a.email?.trim());

        return (
            (!this.currentUser.isLoggedIn && !this.guestOnlyAttendee)
            || (
                !!contactEmail
                && (
                    (hasMyEmail && this.attendees.length > 1 && anyAttendeeHasEmail)
                    || !this.allAttendeesHavePurchaserEmail
                )
            )
        )
    }

    get showCombinedConfirmationInput() {
        const contactEmail = this.currentUser.professionalEmail;
        // 1) If I’m logged‐in, I have an email, but nothing yet in confirmationEmails
        if (contactEmail && this.registration.confirmationEmails.length === 0) {
            return true
        }
        // 2) Otherwise, it’s just the opposite of the individual list case
        return !this.showIndividualConfirmationList
    }
} 